import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import Drawer from '@mui/material/Drawer'
import SubmitButton from 'components/form/SubmitButton'
import { selectCourse } from 'store/course/slice'
import useKeyPress from 'hooks/useKeyPress'
import { IconButton } from '@mui/material'
import CustomTooltip from 'components/ui/CustomTooltip'
import { DrawerToggleRightIcon } from 'assets/icons/drawer-toggle-right'
import { KeyboardArrowDown } from '@mui/icons-material'

interface Props {
  open: boolean
  onClose: () => void
  onToggleSidebar: () => void
  isAdminView: boolean
  onFinishEditing: (anchorEl?: HTMLElement | null) => void
  isFinishEditingDisabled: boolean
  syncSelectedVersion: (version: number) => void
  versionIsDirty: boolean
  resetZoom: () => void
  handleSubmit: (makeOfficial?: boolean, populateRemainingHoles?: boolean) => void
  gpsData: CourseGpsMarker[]
}

export default function CourseGpsEditorSidebar({
  open,
  onClose,
  onToggleSidebar,
  isAdminView,
  onFinishEditing,
  isFinishEditingDisabled,
  syncSelectedVersion,
  versionIsDirty,
  handleSubmit,
  gpsData,
}: Props): JSX.Element {
  const [selectedGpsVersion, setSelectedGpsVersion] = useState<number | null>(null)
  const topSaveButtonRef = useRef<HTMLDivElement | null>(null)

  const { course, gpsVersionStatus } = useSelector(selectCourse)
  const location = useLocation()
  const intl = useIntl()

  const initialGpsVersion = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const version = params.get('version')
    return version ? parseInt(version) : undefined
  }, [location.search])

  const sortedGpsVersions = useMemo(() => {
    const gpsPoints = course?.gpsPoints || []
    const versions = [...new Map(gpsPoints.map((gps: CourseGps) => [gps.gpsVersion, gps])).values()]
    return versions.sort((a, b) => {
      const bVersion = b.gpsVersion || 0
      const aVersion = a.gpsVersion || 0
      return bVersion > aVersion ? 1 : -1
    })
  }, [course])

  const getSelectedVersion = useCallback(() => {
    return selectedGpsVersion !== null ? selectedGpsVersion : sortedGpsVersions[0]?.gpsVersion || 0
  }, [selectedGpsVersion, sortedGpsVersions])

  const getOfficialOrLatestVersion = useCallback(() => {
    const officialVersion = sortedGpsVersions.filter((gps: CourseGps) => gps.isOfficial)
    return officialVersion.length > 0 ? officialVersion[0] : sortedGpsVersions[0]
  }, [sortedGpsVersions])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const version = params.get('version')
    if (version) {
      setSelectedGpsVersion(parseInt(version))
    }
  }, [location.search])

  useEffect(() => {
    if (gpsVersionStatus === 'succeeded') {
      setSelectedGpsVersion(sortedGpsVersions[0]?.gpsVersion || 0)
    }
  }, [sortedGpsVersions, gpsVersionStatus])

  useEffect(() => {
    if (selectedGpsVersion !== null) {
      return
    }
    if (!initialGpsVersion && gpsVersionStatus === 'idle') {
      setSelectedGpsVersion(getOfficialOrLatestVersion()?.gpsVersion || 0)
    }
  }, [getOfficialOrLatestVersion, initialGpsVersion, gpsVersionStatus, selectedGpsVersion])

  useEffect(() => {
    syncSelectedVersion(getSelectedVersion())
  }, [syncSelectedVersion, getSelectedVersion])

  const syncSelectedGpsVersion = (value: number) => {
    setSelectedGpsVersion(value)
  }

  const showInitialLocationButton = () => {
    return course?.gpsPoints && course.gpsPoints.length === 0 && gpsData.length === 2
  }

  const formatVersionDate = (dateValue: Date) => {
    const pad = (value: number) => value.toString().padStart(2, '0')

    const now = new Date()
    const isToday =
      now.getFullYear() === dateValue.getFullYear() &&
      now.getMonth() === dateValue.getMonth() &&
      now.getDate() === dateValue.getDate()

    const time = `${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}`
    if (isToday) {
      return `Today, ${time}`
    }

    const date = `${pad(dateValue.getDate())}.${pad(dateValue.getMonth() + 1)}.${dateValue.getFullYear()}`
    return `${date}, ${time}`
  }

  useKeyPress(['s'], handleSubmit)

  return (
    <Sidebar anchor="right" variant="persistent" open={open} onClose={onClose}>
      <SidebarContent>
        <TopContainer>
          <IconButton color="primary" style={{ margin: '-8px', color: '#fff' }} onClick={onToggleSidebar}>
            <DrawerToggleRightIcon />
          </IconButton>
          <CustomTooltip title={intl.formatMessage({ id: 'strings.sendGpsDataTooltip' })}>
            <div ref={topSaveButtonRef}>
              <TopSaveButton
                status={'idle'}
                handleSubmit={() => onFinishEditing(topSaveButtonRef.current)}
                text={
                  <SaveButtonText>
                    {intl.formatMessage({ id: 'strings.finishEditing' })}
                    {isAdminView && <KeyboardArrowDown fontSize="small" htmlColor="#fff" />}
                  </SaveButtonText>
                }
                disabled={isFinishEditingDisabled}
              />
            </div>
          </CustomTooltip>
        </TopContainer>
        {course?.id && (
          <VersionsSection>
            <VersionsTitle>Version history</VersionsTitle>
            <VersionsList>
              {versionIsDirty && (
                <VersionItem as="div" $selected $interactive={false}>
                  <VersionTopRow>
                    <VersionName>Unsaved changes</VersionName>
                  </VersionTopRow>
                </VersionItem>
              )}
              {sortedGpsVersions.map((gps: CourseGps) => {
                const version = gps.gpsVersion || 0
                const updatedAt = new Date(gps.updatedAt.date)
                const date = formatVersionDate(updatedAt)
                const creator = gps.creatorName || '-'
                const isSelected = !versionIsDirty && getSelectedVersion() === version

                return (
                  <VersionItem
                    key={version}
                    $selected={isSelected}
                    $interactive
                    onClick={() => syncSelectedGpsVersion(version)}
                  >
                    <VersionTopRow>
                      <VersionName>
                        V{version} - {date}
                      </VersionName>
                      {gps.isOfficial && (
                        <OfficialBadge>{intl.formatMessage({ id: 'strings.official' })}</OfficialBadge>
                      )}
                    </VersionTopRow>
                    <VersionMeta>{creator}</VersionMeta>
                  </VersionItem>
                )
              })}
            </VersionsList>
          </VersionsSection>
        )}
        {showInitialLocationButton() && (
          <ActionButtons>
            <SaveInitialButton
              status={gpsVersionStatus}
              text={intl.formatMessage({ id: 'strings.setAsCourseInitialLocation' })}
              handleSubmit={handleSubmit}
            />
          </ActionButtons>
        )}
      </SidebarContent>
    </Sidebar>
  )
}

const Sidebar = styled(Drawer)`
  & .MuiDrawer-paper {
    width: 300px;
    background-color: #272727;
    border-left: none;
    box-sizing: border-box;
    overflow: hidden;
  }
`

const SidebarContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
`

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const VersionsSection = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const VersionsTitle = styled.div`
  color: ${(props) => props.theme.customPalette.light};
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`

const VersionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-right: -16px;
  padding-right: 16px;
`

const VersionItem = styled.button<{ $selected: boolean; $interactive?: boolean }>`
  border: ${(props) => (props.$selected ? '1px solid ' + props.theme.palette.primary.main : '1px solid transparent')};
  background: transparent;
  border-radius: 8px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  cursor: ${(props) => (props.$interactive === false ? 'default' : 'pointer')};
  color: ${(props) => props.theme.customPalette.light};
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.$interactive === false ? 'transparent' : 'rgba(255, 255, 255, 0.04)')};
    border: ${(props) =>
      props.$selected ? '1px solid ' + props.theme.palette.primary.main : '1px solid rgba(255, 255, 255, 0.1)'};
  }
`

const VersionTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`

const VersionName = styled.div`
  font-size: 14px;
  font-weight: 500;
`

const OfficialBadge = styled.span`
  border: 1px solid ${(props) => props.theme.palette.primary.main};
  color: #fff;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 12px;
`

const VersionMeta = styled.div`
  font-size: 12px;
  color: #bbbbbd;
`

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: auto;
`

const SaveButton = styled(SubmitButton)`
  width: 75%;
  height: 47px;
`

const TopSaveButton = styled(SubmitButton)`
  display: flex;
  height: 30px;
  padding: 8px 12px;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  background: #0bb163;
  color: #fff;
  font-weight: 500;
  font-size: 14px;
`

const SaveButtonText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const SaveInitialButton = styled(SaveButton)`
  margin-bottom: 10px;
`
